/**
 * The generic outbox storage seam (WS-20), factored out of the check-in module's original
 * `outbox/store.ts` (WS-13) so a second feature (recipe photos) can reuse the retry/queue
 * machinery instead of forking it. Everything the retry machinery needs from a store is
 * "keep these records until I say otherwise", which is a four-method interface — narrow
 * enough that the IndexedDB wrapper stays honest about how little it does, and every
 * retry/backoff/resume test runs against an in-memory store with no browser in sight.
 *
 * Record *shapes* stay module-specific (check-in's `PhotoOutboxRecord`/
 * `AnnounceOutboxRecord` live in `modules/checkin/outbox/store.ts`; recipes' single-photo
 * record lives in `modules/recipes/outbox/store.ts`) — only the primitives below moved.
 */

export interface OutboxRecord {
  /** Primary key. */
  id: string;
  /** Failed attempts so far — the exponent in the backoff. Survives reloads. */
  attempts: number;
  /** Epoch ms before which this record must not be retried. Survives reloads. */
  nextAttemptAt: number;
}

export interface OutboxStore<T extends OutboxRecord> {
  put(record: T): Promise<void>;
  get(id: string): Promise<T | undefined>;
  all(): Promise<T[]>;
  delete(id: string): Promise<void>;
}

/** In-memory store: tests, and the fallback when IndexedDB is unavailable (private mode). */
export class MemoryOutboxStore<T extends OutboxRecord> implements OutboxStore<T> {
  private readonly records = new Map<string, T>();

  async put(record: T): Promise<void> {
    this.records.set(record.id, record);
  }

  async get(id: string): Promise<T | undefined> {
    return this.records.get(id);
  }

  async all(): Promise<T[]> {
    return [...this.records.values()];
  }

  async delete(id: string): Promise<void> {
    this.records.delete(id);
  }
}
